import { Button, ButtonGroup, Icon } from "@blueprintjs/core";
import * as React from "react";

type Props = {
  numPages: number;
  page: number;
  onPageChange(newPage: number): void;
};

type PageChanger = (event: React.MouseEvent<unknown>) => void;
type PageChangerMaker = (newPage: number) => PageChanger;

export default function Paginator({ page, numPages, onPageChange }: Props) {
  if (numPages <= 1) {
    return null;
  }

  function makePageChanger(newPage: number) {
    return (event: React.MouseEvent<unknown>) => {
      event.preventDefault();
      onPageChange(newPage);
    };
  }

  return (
    <div className="paginator">
      <ButtonGroup className="button-group">
        {page === 1 ? null : (
          <Button key={"paginator-first-page"} onClick={makePageChanger(1)}>
            <span>First</span>
          </Button>
        )}
        {page === 1 ? null : (
          <Button onClick={makePageChanger(page - 1)}>
            <Icon icon="chevron-left" className="black-icon" />
          </Button>
        )}
        <PaginatorButtons page={page} numPages={numPages} makePageChanger={makePageChanger} />
        {page === numPages ? null : (
          <Button onClick={makePageChanger(page + 1)}>
            <Icon icon="chevron-right" className="black-icon" />
          </Button>
        )}
        {page === numPages ? null : (
          <Button key={"paginator-last-page"} onClick={makePageChanger(numPages)}>
            <span>Last</span>
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
}

function PaginatorButtons({
  page,
  numPages,
  makePageChanger,
}: {
  page: number;
  numPages: number;
  makePageChanger: PageChangerMaker;
}) {
  let startPage = 1;
  let endPage = numPages;

  if (numPages > 5) {
    // only show 5 total numbers: 2 below, 2 above
    // unless we hit the min or max number of pages
    // if there are fewer than 5 pages, display all of them

    if (page <= 3) {
      startPage = 1;
      endPage = 5;
    } else if (page + 2 >= numPages) {
      startPage = numPages - 4;
      endPage = numPages;
    } else {
      startPage = page - 2;
      endPage = page + 2;
    }
  }

  const buttons: React.ReactNode[] = [];
  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <Button
        key={`pagination-page-${i}`}
        onClick={makePageChanger(i)}
        className={page === i ? "bp4-active" : ""}
      >
        <span>{i}</span>
      </Button>
    );
  }

  return <>{buttons}</>;
}
